// apps/office/src/pages/office/InvoiceDetail.jsx
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { ArrowLeft, Download, Printer, Share2 } from 'lucide-react';
import { PDFDownloadLink } from '@react-pdf/renderer';
import pb from '../../lib/pocketbaseClient';
import InvoicePDF from '../../components/office/InvoicePDF';
import InteriorInvoicePDF from '../../components/office/InteriorInvoicePDF';
import { pdf } from '@react-pdf/renderer';

const InvoiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [printing, setPrinting] = useState(false);
  const { data: invoice, isLoading, error } = useQuery({
    queryKey: ['invoice', id],
    queryFn: () => pb.collection('invoices').getOne(id, { expand: 'leadId,quotationId,interiorQuotationId' }),
    enabled: !!id,
  });

  const formatPrice = (p) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency', currency: 'INR', maximumFractionDigits: 2,
    }).format(p || 0);

  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
    }) : '—';

  const getStatusBadge = (field) => {
    const colors = {
      Draft: 'bg-gray-100 text-gray-700',
      'Pending Approval': 'bg-yellow-100 text-yellow-700',
      Approved: 'bg-green-100 text-green-700',
      Sent: 'bg-blue-100 text-blue-700',
      Paid: 'bg-emerald-100 text-emerald-700',
    };
    return <Badge className={colors[field] || colors.Draft}>{field || 'Draft'}</Badge>;
  };


  /* ─── Build correct PDF element ─── */
  const buildPdfElement = () => {
    if (!invoice) return null;

    /* Interior invoice */
    if (invoice.source === 'interior' && invoice.interiorQuotationId) {
      if (interiorBundle) {
        return (
          <InteriorInvoicePDF
            quotation={interiorBundle.quotation}
            project={interiorBundle.project}
            client={interiorBundle.client}
            invoiceNumber={invoice.invoiceNumber}
          />
        );
      }
      return (
        <InteriorInvoicePDF
          quotation={null}
          project={null}
          client={null}
          invoiceNumber={invoice.invoiceNumber}
        />
      );
    }

    /* Architecture invoice (existing) */
    return (
      <InvoicePDF
        invoice={pdfInvoice}
        items={items}
        subtotal={subtotal}
        discountAmount={discount}
        taxAmount={tax}
        total={total}
        clientJobIdText={clientJobId}
      />
    );
  };

  const handlePrintPDF = async () => {
    if (!invoice) return;
    setPrinting(true);
    try {
      // Build the same PDF element used by Download PDF
      const doc = buildPdfElement();

      // Generate PDF blob
      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);

      // Create hidden iframe
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      iframe.src = url;
      document.body.appendChild(iframe);

      // Wait for iframe PDF to load, then print
      iframe.onload = () => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } catch (e) {
          console.error('Print error:', e);
          // Fallback: open in new tab
          window.open(url, '_blank');
        }
        // Cleanup after 60s (print dialog may stay open longer)
        setTimeout(() => {
          document.body.removeChild(iframe);
          URL.revokeObjectURL(url);
        }, 60000);
      };
    } catch (err) {
      console.error('PDF print failed:', err);
      toast.error('Print failed: ' + err.message);
    } finally {
      setPrinting(false);
    }
  };

  /* ─── Interior invoice data fetch ─── */
  const interiorQId = invoice?.source === 'interior' ? invoice?.interiorQuotationId : null;
  const { data: interiorBundle } = useQuery({
    queryKey: ['interior-invoice-detail', interiorQId],
    queryFn: async () => {
      if (!interiorQId) return null;
      try {
        const q = await pb.collection('interior_quotations').getOne(interiorQId);
        let proj = null;
        if (q.projectId) {
          try { proj = await pb.collection('interior_projects').getOne(q.projectId); } catch (e) {}
        }
        let cl = null;
        if (proj?.clientId) {
          try { cl = await pb.collection('leads').getOne(proj.clientId); } catch (e) {}
        }
        return { quotation: q, project: proj, client: cl };
      } catch (e) {
        console.error('[InvoiceDetail] Interior data error:', e);
        return null;
      }
    },
    enabled: !!interiorQId,
  });

  if (isLoading) return <div className="flex justify-center items-center h-96">Loading...</div>;
  if (error || !invoice) return (
    <div className="text-center py-16">
      Invoice not found. <Button onClick={() => navigate('/office/invoices')}>Back</Button>
    </div>
  );

  const quotation = invoice.expand?.quotationId || {};
  const lead = invoice.expand?.leadId || {};
  const items = quotation.items || [];
  const subtotal = quotation.subtotal || items.reduce((s, i) => s + (i.amount || 0), 0);
  const discount = invoice.discountAmount || 0;
  const tax = invoice.taxAmount || 0;
  const total = invoice.totalAmount || 0;

  const clientName = lead.name || quotation.clientName || '—';
  const clientPhone = lead.phone || quotation.clientPhone || '—';
  const clientEmail = lead.email || quotation.clientEmail || '—';
  const clientAddress = lead.address || quotation.clientAddress || '—';
  const clientJobId = lead.jobId || '';
  const quotationNumber = quotation.quoteNumber || '—';

  const pdfInvoice = {
    ...invoice,
    clientName, clientPhone, clientEmail, clientAddress,
    clientJobIdText: clientJobId,
    quotationNumber,
    total,
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate('/office/invoices')}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Invoice Details</h1>
            <p className="text-slate-500 text-sm">
              #{invoice.invoiceNumber} · from quotation {quotationNumber}
            </p>
          </div>
        </div>
        <PDFDownloadLink
          document={buildPdfElement()}
          fileName={`invoice_${invoice.invoiceNumber}.pdf`}
        >
          {({ loading }) => (
            <Button variant="outline" className="gap-2" disabled={loading}>
              <Download className="w-4 h-4" /> {loading ? 'Generating...' : 'Download PDF'}
            </Button>
          )}
        </PDFDownloadLink>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle>Invoice Information</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between"><span className="text-slate-500">Invoice Number:</span><span className="font-mono font-semibold">{invoice.invoiceNumber || '—'}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">From Quotation:</span><span className="font-mono text-indigo-600">{quotationNumber}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Date:</span><span>{formatDate(invoice.invoiceDate)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Due Date:</span><span>{formatDate(invoice.dueDate)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Status:</span>{getStatusBadge(invoice.field)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>Client Information</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between"><span className="text-slate-500">Name:</span><span className="font-medium">{clientName}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Phone:</span><span>{clientPhone}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Email:</span><span>{clientEmail}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Address:</span><span className="text-right">{clientAddress}</span></div>
            {clientJobId && (
              <div className="flex justify-between"><span className="text-slate-500">Job ID:</span><span className="font-mono">{clientJobId}</span></div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle>Items</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Item</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Unit</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead className="text-right">Rate</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item, i) => (
                <TableRow key={i}>
                  <TableCell className="font-medium">{item.name}</TableCell>
                  <TableCell>{item.description || '—'}</TableCell>
                  <TableCell>{item.unit || '—'}</TableCell>
                  <TableCell className="text-right">{item.quantity}</TableCell>
                  <TableCell className="text-right">{formatPrice(item.rate)}</TableCell>
                  <TableCell className="text-right">{formatPrice(item.amount)}</TableCell>
                </TableRow>
              ))}
              {items.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center py-6 text-slate-500">No items</TableCell></TableRow>
              )}
            </TableBody>
          </Table>

          <div className="mt-6 flex justify-end">
            <div className="w-80 space-y-2">
              <div className="flex justify-between"><span>Subtotal:</span><span className="font-medium">{formatPrice(subtotal)}</span></div>
              {discount > 0 && <div className="flex justify-between text-green-600"><span>Discount:</span><span>-{formatPrice(discount)}</span></div>}
              {tax > 0 && <div className="flex justify-between"><span>GST:</span><span>+{formatPrice(tax)}</span></div>}
              <div className="border-t pt-2 mt-2">
                <div className="flex justify-between font-bold text-lg">
                  <span>Grand Total:</span>
                  <span className="text-green-600">{formatPrice(total)}</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {invoice.notes && (
        <Card>
          <CardHeader><CardTitle>Notes</CardTitle></CardHeader>
          <CardContent><p className="text-slate-600 whitespace-pre-wrap">{invoice.notes}</p></CardContent>
        </Card>
      )}

      <div className="flex justify-end gap-3">
        <Button
          variant="outline"
          onClick={handlePrintPDF}
          disabled={printing}
        >
          <Printer className="w-4 h-4 mr-2" /> {printing ? 'Preparing…' : 'Print'}
        </Button>
        <Button variant="outline" onClick={() => { navigator.clipboard.writeText(window.location.href); toast.success('Link copied'); }}>
          <Share2 className="w-4 h-4 mr-2" /> Share
        </Button>
      </div>
    </div>
  );
};

export default InvoiceDetail;
